import { Redirect, Route, Switch, useHistory, useLocation, useParams, useRouteMatch } from 'react-router-dom';
import { logger } from '@parrot-software/observability';

import { SubscriptionBlockWizardLayout } from '@subscriptions/presentation/components/base';
import {
  BlockCreationStepCards,
  BlockCreationStepCheckout,
  BlockCreationStepConfirmation,
  BlockCreationStepCreate,
  BlockCreationStepInvoiceData,
  BlockCreationStepPayment,
  CARD_PAYMENT_METHOD_VALUE,
  SPEI_PAYMENT_METHOD_VALUE,
} from '@subscriptions/presentation/components/specific/BlockCreationWizard/steps';
import type {
  CardsRouteState,
  CheckoutRouteState,
  ConfirmationRouteState,
  PaymentMethodValue,
} from '@subscriptions/presentation/components/specific/BlockCreationWizard/steps';

import { isReactivationOrigin, withWizardOrigin } from './wizardOrigin';

const subLog = logger.forDomain('SUB', { subdomain: 'lifecycle' });

const WIZARD_STEP_SEGMENTS = ['payment', 'invoice', 'cards', 'checkout', 'confirmation'];

/**
 * Resolves the active wizard step slug from the pathname for the
 * `closeBlockWizard` abandonment event. The entry route
 * (`/subscriptions/blocks/create`) has no step segment and resolves to `create`.
 *
 * @param pathname - Current `location.pathname`
 * @returns Step slug: `create | payment | invoice | cards | checkout | confirmation`
 */
const resolveWizardStep = (pathname: string): string => {
  const segment = pathname.split('/').filter(Boolean).pop() ?? '';
  return WIZARD_STEP_SEGMENTS.includes(segment) ? segment : 'create';
};

/**
 * Reads the selected payment method from the invoice step's query string
 * (`?method=CARD|SPEI`). The method is carried in the URL — not in router state —
 * so a refresh on the fiscal-data step (the wizard's commit point) preserves the
 * choice instead of bouncing the user back to method selection.
 *
 * @param search - `location.search` of the invoice route
 * @returns The validated method, or null when missing/malformed
 */
const parsePaymentMethod = (search: string): PaymentMethodValue | null => {
  const method = new URLSearchParams(search).get('method');
  if (method === CARD_PAYMENT_METHOD_VALUE || method === SPEI_PAYMENT_METHOD_VALUE) {
    return method;
  }
  return null;
};

/**
 * Inline wrapper that extracts `:blockUuid` from the route and renders the
 * method-selection step (step 2). It only captures the user's choice and then
 * advances to the fiscal-data step (step 3) carrying the selected method in the
 * URL query (`?method=CARD|SPEI`). The actual Stripe / SPEI calls happen in the
 * fiscal-data step, not here.
 *
 * Redirects to the subscriptions list if the param is missing/empty (e.g. a
 * malformed URL like `/subscriptions/blocks/create//payment`) so the step never
 * mounts with an empty uuid.
 */
const BlockCreationStepPaymentRoute = () => {
  const history = useHistory();
  const location = useLocation();
  const { blockUuid } = useParams<{ blockUuid?: string }>();

  if (!blockUuid) {
    return <Redirect to="/subscriptions" />;
  }

  return (
    <BlockCreationStepPayment
      blockUuid={blockUuid}
      onContinue={(method) =>
        history.push(
          withWizardOrigin(`/subscriptions/blocks/create/${blockUuid}/invoice?method=${method}`, location.search),
        )
      }
    />
  );
};

/**
 * Inline wrapper that extracts `:blockUuid` from the route and renders the
 * fiscal-data step (step 3) — the wizard's commit point. The selected method
 * arrives via the `?method=` query; a missing/malformed value sends the user
 * back to method selection (step 2) to choose again, and so does a SPEI method
 * on a reactivation, which is card-only and reachable only by a hand-built URL.
 *
 * On Continuar the step triggers the chosen flow: the SPEI path advances to
 * confirmation (step 5) passing the attached payment method via route state; the
 * card path branches to the saved-cards step (step 3.5) when the license is
 * active and the org has saved cards — passing them via route state — or to the
 * checkout step (step 4) passing the Stripe session via route state.
 *
 * Redirects to the subscriptions list if the uuid is missing/empty so the step
 * never mounts with an empty uuid.
 */
const BlockCreationStepInvoiceDataRoute = () => {
  const history = useHistory();
  const location = useLocation();
  const { blockUuid } = useParams<{ blockUuid?: string }>();

  if (!blockUuid) {
    return <Redirect to="/subscriptions" />;
  }

  // A guard redirect rebuilds the URL, so it has to carry the origin forward: dropping it turns a
  // reactivation into a creation, and the charge resolved downstream is the overdue cycle instead
  // of the reactivation amount. Reactivation is card-only, so a hand-built SPEI link bounces back.
  const backToMethodSelection = withWizardOrigin(`/subscriptions/blocks/create/${blockUuid}/payment`, location.search);

  const selectedMethod = parsePaymentMethod(location.search);
  if (!selectedMethod) {
    return <Redirect to={backToMethodSelection} />;
  }

  if (selectedMethod === SPEI_PAYMENT_METHOD_VALUE && isReactivationOrigin(location.search)) {
    return <Redirect to={backToMethodSelection} />;
  }

  return (
    <BlockCreationStepInvoiceData
      blockUuid={blockUuid}
      onCardSessionReady={(session) =>
        history.push(withWizardOrigin(`/subscriptions/blocks/create/${blockUuid}/checkout`, location.search), {
          clientSecret: session.clientSecret,
          mode: session.mode,
          providerSessionId: session.providerSessionId,
        } as CheckoutRouteState)
      }
      onSavedCardsAvailable={(cards) =>
        history.push(withWizardOrigin(`/subscriptions/blocks/create/${blockUuid}/cards`, location.search), {
          cards,
        } as CardsRouteState)
      }
      onSpeiCompleted={(paymentMethod) =>
        history.push(withWizardOrigin(`/subscriptions/blocks/create/${blockUuid}/confirmation`, location.search), {
          paymentMethod,
          variant: 'spei',
        } as ConfirmationRouteState)
      }
      selectedMethod={selectedMethod}
    />
  );
};

/**
 * Inline wrapper that extracts `:blockUuid` from the route and renders the
 * saved-cards selection step (step 3.5, CARD with active license only).
 *
 * Step 3.5 consumes ephemeral route state (`cards`) produced by the fiscal-data
 * step. On refresh / deep-link that state is gone, so we send the user back to
 * method selection — the same guard idiom as the checkout step. Continuar with a
 * selected card attaches it and advances to confirmation (step 5) with the
 * `card-updated` variant; "Agregar otra tarjeta" follows the existing checkout
 * branch carrying the Stripe session via route state. The confirmation title
 * copy is resolved from the `?origin=` flag, preserved in every push.
 */
const BlockCreationStepCardsRoute = () => {
  const history = useHistory();
  const location = useLocation<CardsRouteState | undefined>();
  const { blockUuid } = useParams<{ blockUuid?: string }>();

  if (!blockUuid) {
    return <Redirect to="/subscriptions" />;
  }

  if (!location.state?.cards) {
    return <Redirect to={withWizardOrigin(`/subscriptions/blocks/create/${blockUuid}/payment`, location.search)} />;
  }

  return (
    <BlockCreationStepCards
      blockUuid={blockUuid}
      cards={location.state.cards}
      onAttached={(paymentMethod) =>
        history.push(withWizardOrigin(`/subscriptions/blocks/create/${blockUuid}/confirmation`, location.search), {
          paymentMethod,
          variant: 'card-updated',
        } as ConfirmationRouteState)
      }
      onCardSessionReady={(session) =>
        history.push(withWizardOrigin(`/subscriptions/blocks/create/${blockUuid}/checkout`, location.search), {
          clientSecret: session.clientSecret,
          mode: session.mode,
          providerSessionId: session.providerSessionId,
        } as CheckoutRouteState)
      }
    />
  );
};

/**
 * Inline wrapper that extracts `:blockUuid` from the route and renders the
 * card checkout step (step 4, CARD only). Guarded like the other steps.
 *
 * Step 4 consumes ephemeral route state (clientSecret/providerSessionId/mode)
 * produced by step 3. On refresh / deep-link that state is gone, so we send the
 * user back to method selection to regenerate the session.
 */
const BlockCreationStepCheckoutRoute = () => {
  const location = useLocation();
  const { blockUuid } = useParams<{ blockUuid?: string }>();

  if (!blockUuid) {
    return <Redirect to="/subscriptions" />;
  }

  if (!location.state) {
    return <Redirect to={withWizardOrigin(`/subscriptions/blocks/create/${blockUuid}/payment`, location.search)} />;
  }

  return <BlockCreationStepCheckout blockUuid={blockUuid} />;
};

/**
 * Inline wrapper that extracts `:blockUuid` from the route and renders the
 * confirmation step (step 5, terminal). Guarded like the other steps.
 *
 * Step 5 reads its payload from ephemeral route state. A refresh here has no
 * state to render, so we fall back to the block detail.
 */
const BlockCreationStepConfirmationRoute = () => {
  const location = useLocation();
  const { blockUuid } = useParams<{ blockUuid?: string }>();

  if (!blockUuid) {
    return <Redirect to="/subscriptions" />;
  }

  if (!location.state) {
    return <Redirect to={`/subscriptions/blocks/${blockUuid}`} />;
  }

  return <BlockCreationStepConfirmation blockUuid={blockUuid} />;
};

/**
 * Orchestrator for the subscription block creation wizard.
 *
 * Mounts the {@link SubscriptionBlockWizardLayout} and an internal `<Switch>` that
 * resolves the active step from the URL. Each step owns its own sub-route so a
 * refresh preserves the user's position in the flow.
 *
 * Steps (URL is the source of truth — the selected method travels in the invoice
 * step's `?method=` query and the entry origin in the `?origin=` query (absent for
 * the creation flow, `update-payment` when re-entering from the block detail to
 * change/configure the payment method, `reactivation` when re-entering to reactivate
 * a churned subscription — see `wizardOrigin.ts`); transient secrets flow via route
 * state; no global wizard context):
 * - `/subscriptions/blocks/create` — Step 1 (create block)
 * - `/subscriptions/blocks/create/:blockUuid/payment` — Step 2 (method selection)
 * - `/subscriptions/blocks/create/:blockUuid/invoice` — Step 3 (fiscal data, commit point)
 * - `/subscriptions/blocks/create/:blockUuid/cards` — Step 3.5 (saved-cards selection, CARD + active license)
 * - `/subscriptions/blocks/create/:blockUuid/checkout` — Step 4 (Stripe checkout, CARD only)
 * - `/subscriptions/blocks/create/:blockUuid/confirmation` — Step 5 (confirmation, terminal)
 * - Catch-all `<Redirect>` sends malformed URLs back to step 1.
 *
 * Back navigation (wizard-level, surfaced top-left on mobile and in the step footers
 * on desktop via the wizard navigation context): forward-only with
 * three exceptions — fiscal data (step 3) returns to method selection (step 2), the
 * saved-cards step (3.5) returns to fiscal data (step 3, CARD branch) and checkout
 * (step 4) returns to fiscal data (step 3). Method selection has no back
 * (its predecessor is block creation, which is not revisitable, and it is also the
 * re-entry landing for changing a payment method).
 *
 * Close (X) behavior: if a `:blockUuid` is present in the URL the user has already
 * created the block, so closing routes to the block detail page; otherwise the user
 * never persisted anything and we route back to the subscriptions list. Closing also
 * emits `closeBlockWizard` tagged with the active step (abandonment funnel).
 */
export const BlockCreationWizard = () => {
  const history = useHistory();
  const location = useLocation();
  const blockMatch = useRouteMatch<{ blockUuid: string }>('/subscriptions/blocks/create/:blockUuid');
  const invoiceMatch = useRouteMatch<{ blockUuid: string }>('/subscriptions/blocks/create/:blockUuid/invoice');
  const cardsMatch = useRouteMatch<{ blockUuid: string }>('/subscriptions/blocks/create/:blockUuid/cards');
  const checkoutMatch = useRouteMatch<{ blockUuid: string }>('/subscriptions/blocks/create/:blockUuid/checkout');

  const handleClose = () => {
    const blockUuid = blockMatch?.params.blockUuid;
    subLog.info('closeBlockWizard', {
      step: resolveWizardStep(location.pathname),
      ...(blockUuid ? { 'block.uuid': blockUuid } : {}),
    });
    if (blockUuid) {
      history.push(`/subscriptions/blocks/${blockUuid}`);
      return;
    }
    history.push('/subscriptions');
  };

  // Wizard-level back action, published through the layout: on mobile it renders in
  // the top chrome bar (aligned with the close X) and on desktop the steps surface it
  // in their `WizardStepFooter` via the wizard navigation context. Only fiscal data
  // (→ method selection), saved cards (→ fiscal data, CARD branch) and checkout
  // (→ fiscal data, always the CARD branch) expose it; every other step leaves it hidden.
  const resolveBack = () => {
    if (checkoutMatch) {
      const { blockUuid } = checkoutMatch.params;
      return () =>
        history.push(
          withWizardOrigin(
            `/subscriptions/blocks/create/${blockUuid}/invoice?method=${CARD_PAYMENT_METHOD_VALUE}`,
            location.search,
          ),
        );
    }
    if (cardsMatch) {
      const { blockUuid } = cardsMatch.params;
      return () =>
        history.push(
          withWizardOrigin(
            `/subscriptions/blocks/create/${blockUuid}/invoice?method=${CARD_PAYMENT_METHOD_VALUE}`,
            location.search,
          ),
        );
    }
    if (invoiceMatch) {
      const { blockUuid } = invoiceMatch.params;
      return () => history.push(withWizardOrigin(`/subscriptions/blocks/create/${blockUuid}/payment`, location.search));
    }
    return undefined;
  };
  const handleBack = resolveBack();

  return (
    <SubscriptionBlockWizardLayout onBack={handleBack} onClose={handleClose}>
      <Switch>
        <Route exact path="/subscriptions/blocks/create">
          <BlockCreationStepCreate
            onCreated={(blockUuid) => history.push(`/subscriptions/blocks/create/${blockUuid}/payment`)}
          />
        </Route>

        <Route exact path="/subscriptions/blocks/create/:blockUuid/payment">
          <BlockCreationStepPaymentRoute />
        </Route>

        <Route exact path="/subscriptions/blocks/create/:blockUuid/invoice">
          <BlockCreationStepInvoiceDataRoute />
        </Route>

        <Route exact path="/subscriptions/blocks/create/:blockUuid/cards">
          <BlockCreationStepCardsRoute />
        </Route>

        <Route exact path="/subscriptions/blocks/create/:blockUuid/checkout">
          <BlockCreationStepCheckoutRoute />
        </Route>

        <Route exact path="/subscriptions/blocks/create/:blockUuid/confirmation">
          <BlockCreationStepConfirmationRoute />
        </Route>

        <Redirect to="/subscriptions/blocks/create" />
      </Switch>
    </SubscriptionBlockWizardLayout>
  );
};
